/**
 * BillingSuccess - Stripe checkout success page
 * 
 * Displayed after successful payment. Shows confirmation and redirects appropriately
 * based on user type (consultant vs client).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useBilling } from '../contexts/BillingContext';
import { useCompany } from '../contexts/CompanyContext';

const BillingSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshBilling } = useBilling();
  const { isConsultantUser, selectedCompany } = useCompany();
  const [countdown, setCountdown] = useState(5);

  const sessionId = searchParams.get('session_id');

  // Determine redirect URL based on user type
  const getRedirectUrl = () => {
    if (isConsultantUser) {
      // Consultants go to admin billing settings
      return '/admin?tab=settings';
    } else if (selectedCompany) {
      // Clients go to their company settings with billing tab
      return `/company/${selectedCompany.id}/settings?tab=billing`;
    } else {
      // Fallback to dashboard
      return '/';
    }
  };

  const redirectUrl = getRedirectUrl();
  const buttonLabel = isConsultantUser ? 'Go to Billing Settings' : 'Go to Dashboard';

  // Refresh billing status and countdown
  useEffect(() => {
    // Refresh billing data
    refreshBilling();

    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(redirectUrl);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [refreshBilling, navigate, redirectUrl]);

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="80vh"
      >
        <Paper
          elevation={3}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
          }}
        >
          <CheckCircleIcon
            sx={{
              fontSize: 80,
              color: 'success.main',
              mb: 2,
            }}
          />
          
          <Typography variant="h4" gutterBottom fontWeight="bold" color="success.dark">
            Payment Successful!
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Thank you for your purchase. Your account has been updated.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Redirecting in {countdown} seconds...
          </Typography>

          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => navigate(redirectUrl)}
          >
            {buttonLabel}
          </Button>
        </Paper>
      </Box>
    </Container>
  );
};

export default BillingSuccess;

